import {
  Component, OnDestroy, ViewEncapsulation,
  signal, computed, effect, inject, afterNextRender,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject, from } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { BoService } from '../services/bo.service';
import {
  DashboardStats, ScanRowVm, RecentControlVm,
  ConfigStepVm, RawBucket, DkEntry, TplScanEntry,
  toRecentControls, computeTplStats,
  toTemplateGauges, computeAccessGauges, computeOvermax,
  rollUpBuckets, fmt, sortPropScan,
  MAX_M, MAX_H, MAX_D, SYNTHETIC_ACCESS_IDS, TimelineDisplay,
} from './tableau-de-bord.types';
import { DashboardDonutsComponent } from './dashboard-donuts/dashboard-donuts.component';
import { DashboardScanListComponent } from './dashboard-scan-list/dashboard-scan-list.component';
import { DashboardStepsComponent } from './dashboard-steps/dashboard-steps.component';
import { DashboardCarouselComponent } from './dashboard-carousel/dashboard-carousel.component';
import { DashboardTimelineComponent } from './dashboard-timeline/dashboard-timeline.component';

// ---- Types internes ---------------------------------------------------------

interface LoadedData {
  listId: string; listName: string; listDate: string;
  toTick: number; toScan: number; toScanTxt: string;
  totS: number; totalSTxt: string; totSPer: string;
  totIn: number; totalInTxt: string; totInPer: string;
  totOut: number; totalOutTxt: string; totOutPer: string;
  accessRows: ScanRowVm[];
  tarifsRows: ScanRowVm[];
  tplListb: { name: string; scanNbr: string }[];
  recentS: RecentControlVm[];
  recentIn: RecentControlVm[];
  recentOut: RecentControlVm[];
  minuteBuckets: RawBucket[];
  overmax: number;
  configSteps: ConfigStepVm[];
  progressPct: number;
  carouselItems: string[];
  nbAccess: number;
}

// ---- Component --------------------------------------------------------------

@Component({
  selector: 'app-tableau-de-bord',
  standalone: true,
  templateUrl: './tableau-de-bord.component.html',
  styleUrls: ['./tableau-de-bord.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterLink,
    DashboardDonutsComponent,
    DashboardScanListComponent,
    DashboardStepsComponent,
    DashboardCarouselComponent,
    DashboardTimelineComponent,
  ],
})
export class TableauDeBordComponent implements OnDestroy {
  private readonly boService = inject(BoService);
  private readonly router    = inject(Router);

  // Anti-race: BehaviorSubject -> switchMap -> toSignal
  private readonly listId$ = new BehaviorSubject<string>('');
  private destroyed = false;
  private watchTimerId: ReturnType<typeof setInterval> | null = null;

  // Pas de distinctUntilChanged : onRefresh ré-émet volontairement le même id
  // (le poll de startLoad garde déjà `current !== id` pour les vrais changements).
  private readonly loaded = toSignal<LoadedData | null>(
    this.listId$.pipe(
      filter(id => !!id),
      switchMap(id =>
        from(
          Promise.all([this.boService.getAllKeys(), this.boService.getDashboardStats(id)])
            .then(([dk, stats]) => this.buildLoadedData(id, dk as DkEntry[], stats as DashboardStats))
        )
      ),
    ),
    { initialValue: null },
  );

  // UI signals
  readonly cgs           = signal<number>(0);
  readonly activePanel   = signal<'access' | 'tarif'>('tarif');
  readonly b2Display     = signal<TimelineDisplay>({
    titb2: 'Nombre<span class="respdis13"> d\'entrées</span>',
    mper: '0', mtntot: '0', texttotparT: '0',
    d1: 0, d2: 1, // donut vide au chargement (iso legacy : séries initiales [0, 1])
  });
  /** Série du donut pilotée par le type sélectionné dans la timeline (iso legacy). */
  readonly donutSeries = computed(() => ({ d1: this.b2Display().d1, d2: this.b2Display().d2 }));

  // Computed from loaded data
  readonly listName      = computed(() => this.loaded()?.listName   ?? '...');
  readonly listDate      = computed(() => this.loaded()?.listDate   ?? '');
  readonly toScanTxt     = computed(() => this.loaded()?.toScanTxt  ?? '0');
  readonly accessRows    = computed(() => this.loaded()?.accessRows ?? []);
  /** Sens du tri des jauges par tarif, bascule par .btntritpltdb. */
  readonly tarifSortDir = signal<'asc' | 'desc'>('desc');
  readonly tarifsRows    = computed(() =>
    sortPropScan(this.loaded()?.tarifsRows ?? [], 'propScan', this.tarifSortDir()));
  readonly configSteps   = computed(() => this.loaded()?.configSteps ?? Array.from({ length: 5 }, () => ({ done: false } as ConfigStepVm)));
  readonly progressPct   = computed(() => this.loaded()?.progressPct ?? 0);
  readonly carouselItems = computed(() => this.loaded()?.carouselItems ?? []);
  readonly minuteBuckets = computed(() => this.loaded()?.minuteBuckets ?? []);
  readonly overmax       = computed(() => this.loaded()?.overmax ?? 0);
  readonly recentS       = computed(() => this.loaded()?.recentS  ?? []);
  readonly recentIn      = computed(() => this.loaded()?.recentIn ?? []);
  readonly recentOut     = computed(() => this.loaded()?.recentOut ?? []);
  readonly totalSTxt     = computed(() => this.loaded()?.totalSTxt  ?? '0');
  readonly totalInTxt    = computed(() => this.loaded()?.totalInTxt ?? '0');
  readonly totalOutTxt   = computed(() => this.loaded()?.totalOutTxt ?? '0');
  readonly totSPer       = computed(() => this.loaded()?.totSPer  ?? '0');
  readonly totInPer      = computed(() => this.loaded()?.totInPer ?? '0');
  readonly totOutPer     = computed(() => this.loaded()?.totOutPer ?? '0');
  readonly toScan        = computed(() => this.loaded()?.toScan ?? 0);
  readonly totIn         = computed(() => this.loaded()?.totIn   ?? 0);
  readonly totS          = computed(() => this.loaded()?.totS    ?? 0);
  readonly totOut        = computed(() => this.loaded()?.totOut  ?? 0);
  // Iso legacy : la liste des tarifs du bloc B1 est toujours rendue en entier ;
  // « Tout afficher » n'apparaît qu'au-delà de 100 tarifs et mène à configurer-ma-liste.
  readonly tplListbVisible = computed(() => this.loaded()?.tplListb ?? []);
  readonly tplListbHasMore = computed(() => (this.loaded()?.tplListb.length ?? 0) > 100);
  readonly tplCount  = computed(() => this.loaded()?.tplListb.length ?? 0);
  readonly toTick    = computed(() => this.loaded()?.toTick ?? 0);
  readonly toTickTxt = computed(() => fmt(this.toTick()));
  readonly nbAccess  = computed(() => this.loaded()?.nbAccess ?? 0);
  /** 'DD-MM-YYYY HH:mm:ss' -> { date: 'DD/MM/YYYY', hour: 'HHhMM' } (iso legacy). */
  readonly listDateFmt = computed(() => {
    const raw = this.loaded()?.listDate ?? '';
    const [datePart, timePart] = raw.indexOf(' ') > -1 ? raw.split(' ') : ['', '00:00'];
    const hvm = (timePart || '00:00').split(':');
    return {
      date: (datePart || '').replace(/-/g, '/'),
      hour: (hvm[0] ?? '00') + 'h' + (hvm[1] ?? '00'),
    };
  });
  /** Toggle du bloc "Ma liste" (liste des tarifs vs bloc Informations, iso legacy). */
  readonly b3Expanded = signal<boolean>(false);
  /** Vrai après le premier toggle — avant, aucun style inline (le CSS responsive garde la main). */
  readonly b3Touched = signal<boolean>(false);
  /** Largeur figée au chargement (iso legacy — pas de re-layout au resize). */
  readonly wiw = window.innerWidth;

  constructor() {
    // Loader fadeout when data arrives
    effect(() => {
      if (this.loaded() !== null) {
        const bl = document.querySelector('.blocLoader') as HTMLElement | null;
        if (bl) setTimeout(() => { bl.style.opacity = '0'; setTimeout(() => { bl.style.display = 'none'; }, 300); }, 300);
      }
    });

    afterNextRender(() => {
      this.setupShell();
      try {
        const info = JSON.parse(localStorage.getItem('infoUser') || '{}') as Record<string, string>;
        this.boService.screenMode(
          info['darkMode']     === 'on' ? 'on' : 'off',
          info['contrastMode'] === 'on' ? 'on' : 'off',
        );
      } catch { /* localStorage unavailable */ }
      this.startLoad();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.watchTimerId !== null) { clearInterval(this.watchTimerId); this.watchTimerId = null; }
    window.removeEventListener('resize', this.applyBodyOverflow);
  }

  /** Shell legacy (header/menu partagés hors composant) — même pattern que configurer-ma-liste. */
  private setupShell(): void {
    const menubo = document.querySelector('.menubo') as HTMLElement | null;
    const headerbo = document.querySelector('.headerbo') as HTMLElement | null;
    if (menubo) menubo.style.display = 'flex';
    if (headerbo) headerbo.style.display = 'flex';

    const htitsec = document.getElementById('htitsec');
    const htitsec2 = document.getElementById('htitsec2');
    if (htitsec) htitsec.innerHTML = 'Accueil';
    if (htitsec2) htitsec2.innerHTML = 'Tableau de bord';

    document.querySelectorAll('.menubs').forEach(el => ((el as HTMLElement).id = ''));
    const menubs1 = document.querySelector('.menubs-1');
    if (menubs1) menubs1.id = 'actif1';
    document.querySelectorAll('.secmenu2bs').forEach(el => ((el as HTMLElement).id = ''));
    const s1 = document.getElementsByClassName('1s1')[0] as HTMLElement | undefined;
    if (s1) s1.id = 'actif2';

    this.applyBodyOverflow();
    window.addEventListener('resize', this.applyBodyOverflow);
    window.scrollTo(0, 0);
  }

  /**
   * Le legacy figeait overflow:hidden au chargement (wiw > 820) : réduire la fenêtre
   * ensuite rendait le bas du dashboard inaccessible (aucun scroll). Recalculé au
   * resize. Quand la hauteur ne suffit plus au layout 100vh, on reprend le mécanisme
   * du breakpoint ≤830 du CSS legacy : .cgs passe en fit-content, ce qui étire le
   * cadre ui-app-layout avec le contenu — le scroll reste DANS le cadre.
   */
  private readonly applyBodyOverflow = (): void => {
    // ≤1115px la colonne donut/jauges passe sous les deux premières (CSS) : le
    // layout est plus haut que le viewport, le scroll est donc toujours requis.
    const fitsDesktopLayout = window.innerWidth > 1115 && window.innerHeight >= 800;
    document.body.style.overflow = fitsDesktopLayout ? 'hidden' : 'auto';
    const cgs = document.querySelector('.cgs') as HTMLElement | null;
    if (cgs) cgs.style.height = fitsDesktopLayout ? '' : 'fit-content';
  };

  private async startLoad(): Promise<void> {
    let id = '';
    let attempts = 0;
    while (!id && attempts < 40 && !this.destroyed) {
      id = this.boService.getCurrentList()._id ?? '';
      if (!id) await new Promise<void>(r => setTimeout(r, 300));
      attempts++;
    }
    if (!id || this.destroyed) return;

    this.listId$.next(id);

    if (this.watchTimerId !== null) clearInterval(this.watchTimerId);
    this.watchTimerId = setInterval(() => {
      if (this.destroyed) { clearInterval(this.watchTimerId!); return; }
      const current = this.boService.getCurrentList()._id ?? '';
      if (current && current !== id) {
        id = current;
        this.listId$.next(id);
      }
    }, 1000);
  }

  private buildLoadedData(listId: string, rawDk: DkEntry[], stats: DashboardStats): LoadedData {
    const dataList  = this.boService.getCurrentList();
    const listName  = dataList?.name ?? '';
    const listDate  = dataList?.dateEndValide ?? '';
    const tplRaw: TplScanEntry[] = (Array.isArray(dataList?.tpl) ? dataList.tpl : [])
      .map(t => ({ ...t, name: t.name ?? '' }));

    const { tplScan, toTick, toScan } = computeTplStats(tplRaw, stats.ticketsByTemplate, stats.scansByTemplate);
    const totIn  = stats.totals?.in  ?? 0;
    const totOut = stats.totals?.out ?? 0;
    const totS   = totIn - totOut;

    const toScanTxt   = fmt(toScan);
    const totalSTxt   = fmt(totS);
    const totalInTxt  = fmt(totIn);
    const totalOutTxt = fmt(totOut);
    const totSPer   = toScan > 0 ? ((totS  / toScan) * 100).toFixed(0) : '0';
    const totInPer  = toScan > 0 ? ((totIn / toScan) * 100).toFixed(0) : '0';
    const totOutPer = totIn  > 0 ? ((totOut / totIn) * 100).toFixed(0)  : '0';

    const accessRows = computeAccessGauges(rawDk, stats.scansByAccess, listId, totS);
    const nbAccess   = accessRows.filter(r => r.id !== SYNTHETIC_ACCESS_IDS[0] && r.id !== SYNTHETIC_ACCESS_IDS[1]).length;
    const tarifsRows = toTemplateGauges(tplScan);
    // Iso legacy (.listplb) : nombre de scans autorisés par tarif, 'Illimité' si infinity.
    const tplListb   = tplScan.map(t => ({
      name: t.name,
      scanNbr: (t.infinity === true || t.infinity === 'true') ? 'Illimité' : fmt(t.scanNbr ?? 0),
    }));

    const tplNameMap: Record<string, string> = {};
    for (const t of tplRaw) tplNameMap[t.id] = t.name;
    const allRecent = toRecentControls(stats.recentEvents ?? [], tplNameMap);

    const minuteBuckets: RawBucket[] = stats.timeSeries ?? [];
    const hourBuckets   = rollUpBuckets(minuteBuckets, 'H');
    const dayBuckets    = rollUpBuckets(minuteBuckets, 'J');
    const overmax = computeOvermax(minuteBuckets.length, hourBuckets.length, dayBuckets.length, MAX_M, MAX_H, MAX_D);

    const stepconf = [1, 1, tplRaw.length > 0 ? 1 : 0, rawDk.length > 0 ? 1 : 0, nbAccess > 0 ? 1 : 0];
    const configSteps: ConfigStepVm[] = stepconf.map(d => ({ done: d === 1 }));
    const progressPct = Math.round((stepconf.filter(s => s === 1).length / stepconf.length) * 100);
    const truncName   = listName.length > 25 ? listName.substring(0, 23) + '...' : listName;

    return {
      listId, listName: truncName, listDate,
      toTick, toScan, toScanTxt,
      totS, totalSTxt, totSPer,
      totIn, totalInTxt, totInPer,
      totOut, totalOutTxt, totOutPer,
      accessRows, tarifsRows, tplListb,
      recentS: allRecent,
      recentIn: allRecent.filter(r => r.isIn),
      recentOut: allRecent.filter(r => !r.isIn),
      minuteBuckets, overmax, configSteps, progressPct,
      carouselItems: this.boService.bas ?? [],
      nbAccess,
    };
  }

  toggleCgs():         void { this.cgs.set(this.cgs() === 0 ? 1 : 0); }
  toggleB3():          void { this.b3Touched.set(true); this.b3Expanded.set(!this.b3Expanded()); }
  onSwitchToAccess():  void { this.activePanel.set('access'); }
  /** Inverse le tri par proportion controlee. */
  toggleTarifSort(): void {
    this.tarifSortDir.update(d => d === 'desc' ? 'asc' : 'desc');
  }

  onSwitchToTarif():   void { this.activePanel.set('tarif'); }
  onTimelineDisplay(d: TimelineDisplay): void { this.b2Display.set(d); }
  onEditList():        void { this.router.navigateByUrl('/configurer-general'); }
  onConfigList():      void { this.router.navigateByUrl('/configurer-ma-liste'); }

  onRefresh(): void {
    const id = this.listId$.value;
    if (id) { this.listId$.next(''); setTimeout(() => this.listId$.next(id), 0); }
  }
}
